import type { SessionMessageEntry } from '@erp/rpc';
import { actionDetail } from 'src/components/common/agentActionFormatting';
import { actorOf } from './traceDetail';
import type { Span } from './types';
import { formatToolDisplayName } from './utils';

// ---------------------------------------------------------------------------
// The operator's individual actions, off the live stream, as trace rows.
//
// A streamed action carries NO parent: `on_step` (ai-service adapter.py) emits `action` +
// `operatorStep` and nothing that names the procedure step it belongs to, and the two replay
// boundaries that DO name the step are suppressed as bubbles and folded into `sopReplay` state
// (operatorAgUiAdapter.ts `isSuppressedBubble`). So the message list carries no step markers to
// scan either.
//
// What is available is `sopReplay.running` — the step executing right now. Attributing each action
// to whatever was running when it FIRST appeared is therefore an inference from arrival order, not
// a recorded fact. Two consequences, both deliberate:
//
//   * An action landing in the gap between two steps can attach to the neighbour.
//   * After a reconnect the stream may resend the whole history at once; those messages have no
//     recorded step and attach to whatever is running then. They're placeholders that vanish when
//     the persisted rows land, so a wrong guess is transient — but it is a guess.
//
// The durable fix is upstream: stamp the SOP step id onto the action's own metadata in
// `StepEmitter.on_step`, which already knows it. Then this becomes a lookup.
// ---------------------------------------------------------------------------

/** Parent key for actions seen while no procedure step was running. */
export const UNASSIGNED_PARENT = '__live_unassigned__';

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * The action's arguments — the key pressed, the text typed, the coordinate clicked.
 *
 * `makeOperatorStepPump` projects them onto `actionArgs`, because the canonical `operatorStep` that
 * originally carries them is stripped from the live bag (`omitOperatorStep: true`). The
 * `operatorStep` read is the fallback for any path that does hand the canonical step over intact.
 */
function streamedActionArgs(
  metadata: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  const projected = record(metadata?.actionArgs);
  if (projected) return projected;
  const step = record(metadata?.operatorStep);
  const actions = step?.actions;
  if (!Array.isArray(actions)) return undefined;
  return record(record(actions[0])?.args) ?? undefined;
}

function streamedScreenshot(
  metadata: Record<string, unknown> | undefined,
): string | null {
  const step = record(metadata?.operatorStep);
  const actions = step?.actions;
  if (!Array.isArray(actions)) return null;
  const result = record(record(actions[0])?.result);
  const ref = result?.screenshotRef;
  return typeof ref === 'string' && ref ? ref : null;
}

/** A stable key for a streamed message — `messageId` when the operator set one, else its id. */
export function liveMessageKey(message: SessionMessageEntry): string {
  return message.messageId ?? message.id;
}

export function liveActionSpan(
  message: SessionMessageEntry,
  /** The buffered prose that preceded this action — see `groupLiveActions`. */
  pendingReasoning?: string | null,
): Span {
  const action = message.action ?? '';
  const args = streamedActionArgs(message.metadata);
  return {
    // Namespaced so it can never collide with a persisted tool-call id — these rows are
    // placeholders and must not be mistaken for the real thing by the selection or focus code.
    id: `live:${liveMessageKey(message)}`,
    type: 'tool',
    name: formatToolDisplayName(action || 'action'),
    action,
    detail: actionDetail(action, args),
    // The prose that arrived just before this action, else whatever text the action carried
    // itself. Mirrors `makeOperatorStepPump`'s `pendingReasoning || redacted`, which is what the
    // persisted row records — so a row keeps its words when its durable twin replaces it.
    reasoning: pendingReasoning?.trim() || message.message?.trim() || null,
    // Same tag the persisted row is badged from, so the live row is badged the same way.
    actor: actorOf(message.metadata, action),
    status: message.level === 'error' ? 'failed' : 'completed',
    startMs: 0,
    durMs: null,
    screenshotSignedUrl: streamedScreenshot(message.metadata),
  };
}

/**
 * Group streamed actions by the step-span id they belong to.
 *
 * `assign` records the parent for a message the FIRST time it is seen and never revises it — the
 * running step moves on, and a row must not migrate under a later step just because it was
 * re-rendered. Callers own that map so it survives across renders.
 */
export function groupLiveActions(
  messages: readonly SessionMessageEntry[],
  parentByMessage: Map<string, string>,
  currentParent: string,
): Map<string, Span[]> {
  const byParent = new Map<string, Span[]>();
  // The operator flushes a turn's reasoning as its OWN action-less message immediately before the
  // action it explains (`adapter.py on_message_delta` → `on_step`). Buffer it and hand it to the
  // next action, exactly as `makeOperatorStepPump` does for the durable row — the live bag can't
  // supply it any other way, since `projectSkillSignalMetadata(..., { omitOperatorStep: true })`
  // strips the canonical step that carries it. Skipping these messages outright, as this loop
  // used to, is why a live row sat wordless until its persisted twin landed.
  let pendingReasoning: string | null = null;
  for (const message of messages) {
    if (!message.action) {
      // Rendering it as its own row would duplicate the text onto the action below it.
      pendingReasoning = message.message?.trim() || pendingReasoning;
      continue;
    }
    const key = liveMessageKey(message);
    let parent = parentByMessage.get(key);
    if (parent === undefined) {
      parent = currentParent;
      parentByMessage.set(key, parent);
    }
    const span = liveActionSpan(message, pendingReasoning);
    pendingReasoning = null;
    const list = byParent.get(parent);
    if (list) list.push(span);
    else byParent.set(parent, [span]);
  }
  return byParent;
}
